import MonitorIcon from '@mdi/svg/svg/monitor.svg?react';
import StoreIcon from '@mdi/svg/svg/store.svg?react';
import { Button, Flex, Grid, MDIIcon } from '@solinkcorp/solink-components';
import { areTimezonesSameTime, getLocalTimeZone } from '@solinkcorp/utils';
import classNames from 'classnames';
import { type IEvent } from 'clients/PlatformApi';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import * as EventUtil from 'utils/EventUtil';
import { type VideoDiscoverySite } from 'utils/types';
import FormattedTime from '../../FormattedTime';
import styles from './EventTimeLocation.module.sass';

interface EventTimeLocationProps {
  event: IEvent;
  selectedLocationsTimezoneId: string;
  locationTimezone: string;
  location: VideoDiscoverySite | undefined;
  colorMode?: 'light' | 'dark';
}

const EventTimeLocation: React.FC<EventTimeLocationProps> = (props) => {
  const {
    event,
    selectedLocationsTimezoneId,
    locationTimezone,
    location,
    colorMode = 'light',
  } = props;
  const locale = useLocale();

  const isAlarm = EventUtil.isAlarm(event);

  const locationName = location?.name || '...';
  const locationAddress = location?.address || '...';

  const shouldDisplayBrowserTimezone = !areTimezonesSameTime(
    locationTimezone,
    selectedLocationsTimezoneId,
    event.startTime,
  );

  const dateLocationWrapperClass = classNames(styles[colorMode], {
    [styles['with-device-time']]: shouldDisplayBrowserTimezone,
  });

  return (
    <Grid
      className={dateLocationWrapperClass}
      gridTemplateColumns={isAlarm ? '1fr' : `175px 1fr`}
      gap="0 12px"
    >
      {isAlarm ? (
        <div className="alarm-address-date" style={{ justifySelf: 'flex-start' }}>
          <div className={styles['location-address']} title={locationAddress}>
            {locationAddress}
          </div>
          <div className={styles['event-date']} data-testid="event-card-date">
            <FormattedTime
              date={event.startTime}
              withDate="only"
              timezoneId={locationTimezone}
              dateFormatString="z - DD MMM, YYYY"
            />
          </div>
        </div>
      ) : (
        <>
          <Flex flexDirection="column">
            {/* event.startTime has no timezone info, so it'll be in the browser's timezone */}
            {shouldDisplayBrowserTimezone && (
              <Flex
                className={styles['event-date']}
                data-testid="event-card-date"
                width="auto"
                alignItems="center"
              >
                <Button
                  className={styles['timezone-info']}
                  icon={<MDIIcon icon={MonitorIcon} size={13} />}
                  iconOnly={true}
                  tooltip={locale.general.browserTime}
                  tooltipStyle={{ whiteSpace: 'nowrap' }}
                  type="clear"
                />
                <FormattedTime
                  date={event.startTime}
                  withDate
                  seconds
                  hideDateTimezone
                  timezoneId={getLocalTimeZone()}
                />
              </Flex>
            )}
            <Flex
              className={styles['event-date']}
              data-testid="event-card-date"
              width="auto"
              alignItems="center"
            >
              {shouldDisplayBrowserTimezone && (
                <Button
                  className={styles['timezone-info']}
                  icon={<MDIIcon icon={StoreIcon} size={15} />}
                  iconOnly={true}
                  tooltip={locale.general.locationTime}
                  tooltipStyle={{ whiteSpace: 'nowrap' }}
                  type="clear"
                />
              )}
              <FormattedTime
                date={event.startTime}
                timezoneId={locationTimezone}
                withDate
                hideDateTimezone
                seconds
              />
            </Flex>
          </Flex>
          <div className={styles['location-name']} title={locationName}>
            {locationName}
          </div>
        </>
      )}
    </Grid>
  );
};

export default EventTimeLocation;
